import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, CalendarDays, Trash2, Loader2, Share2, Download, Copy, Check, Apple, ExternalLink, Pencil, Clock, MapPin, ArrowRight } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { buildGoogleCalendarUrl } from '@/lib/googleCalendar';
import { format, parseISO, isSameDay } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CalendarPage() {
  const { user } = useAuth();
  const canCreate = user?.role === 'admin' || user?.role === 'member' || user?.role === 'guest';
  const isAdmin = user?.role === 'admin';
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('18:00');
  const [eventEndTime, setEventEndTime] = useState('19:00');
  const [eventAllDay, setEventAllDay] = useState(false);
  const [eventLocation, setEventLocation] = useState('');
  const [creating, setCreating] = useState(false);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [subscribeUrls, setSubscribeUrls] = useState({ subscribe_url: null, webcal_url: null });
  const [generatingToken, setGeneratingToken] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const fetchSubscribeUrls = async () => {
    try {
      const response = await axios.get(`${API}/family/calendar/token`, { withCredentials: true });
      setSubscribeUrls(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar subscribe URLs:', error);
    }
  };

  const handleGenerateSubscribeLink = async () => {
    setGeneratingToken(true);
    try {
      const response = await axios.post(`${API}/family/calendar/token`, {}, { withCredentials: true });
      setSubscribeUrls(response.data);
      toast.success('Subscription link generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate link');
    } finally {
      setGeneratingToken(false);
    }
  };

  const openSubscribeDialog = async () => {
    await fetchSubscribeUrls();
    setSubscribeDialogOpen(true);
  };

  const copySubscribeUrl = async () => {
    if (!subscribeUrls.subscribe_url) return;
    try {
      await navigator.clipboard.writeText(subscribeUrls.subscribe_url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDownloadIcs = async (eventId, eventTitleStr) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/ics`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const safeTitle = (eventTitleStr || 'event').replace(/[^a-z0-9-_ ]/gi, '').trim() || 'event';
      // Use a data: URL so iOS WKWebView (Capacitor) hands the .ics to Calendar.
      const text = await response.data.text();
      const dataUrl = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(text);
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${safeTitle}.ics`;
      a.rel = 'noopener';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Event downloaded! Open it to add to your calendar.');
    } catch (error) {
      toast.error('Failed to download event');
    }
  };

  const resetEventForm = () => {
    setEditingEventId(null);
    setEventTitle('');
    setEventDescription('');
    setEventDate('');
    setEventStartTime('18:00');
    setEventEndTime('19:00');
    setEventAllDay(false);
    setEventLocation('');
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventTitle.trim() || !eventDate) return;

    setCreating(true);
    try {
      let startIso = eventDate;
      let endIso = null;
      if (!eventAllDay) {
        startIso = `${eventDate}T${eventStartTime}:00`;
        endIso = `${eventDate}T${eventEndTime}:00`;
      }
      
      const payload = {
        title: eventTitle.trim(),
        description: eventDescription.trim(),
        start_date: startIso,
        end_date: endIso,
        all_day: eventAllDay,
        location: eventLocation.trim(),
      };
      
      let response;
      if (editingEventId) {
        response = await axios.put(`${API}/events/${editingEventId}`, payload, { withCredentials: true });
        setEvents(prev => prev.map(ev => ev.id === editingEventId ? response.data : ev));
        toast.success('Event updated!');
      } else {
        response = await axios.post(`${API}/events`, payload, { withCredentials: true });
        setEvents(prev => [...prev, response.data]);
        toast.success('Event created!');
      }
      
      resetEventForm();
      setCreateDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const openCreateDialog = (date) => {
    resetEventForm();
    if (date) {
      setEventDate(format(date, 'yyyy-MM-dd'));
      setSelectedDate(date);
    } else {
      setEventDate(format(new Date(), 'yyyy-MM-dd'));
    }
    setCreateDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setEditingEventId(event.id);
    setEventTitle(event.title);
    setEventDescription(event.description || '');
    // Parse the ISO date/datetime
    const start = event.start_date;
    if (event.all_day) {
      setEventDate(start.substring(0, 10));
      setEventStartTime('18:00');
      setEventEndTime('19:00');
    } else {
      setEventDate(start.substring(0, 10));
      // Extract HH:MM from ISO string
      const timeMatch = start.match(/T(\d{2}:\d{2})/);
      setEventStartTime(timeMatch ? timeMatch[1] : '18:00');
      if (event.end_date) {
        const endMatch = event.end_date.match(/T(\d{2}:\d{2})/);
        setEventEndTime(endMatch ? endMatch[1] : '19:00');
      }
    }
    setEventAllDay(event.all_day === true);
    setEventLocation(event.location || '');
    setCreateDialogOpen(true);
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      try {
        const eventDateObj = parseISO(event.start_date);
        return isSameDay(eventDateObj, date);
      } catch {
        return false;
      }
    });
  };

  const selectedDateEvents = getEventsForDate(selectedDate);

  // Format "HH:MM" (24h) into "h:mm a" (12h). Fallback to the raw value on error.
  const fmtTime = (hhmm) => {
    if (!hhmm || !/^\d{2}:\d{2}/.test(hhmm)) return hhmm || '';
    const [hh, mm] = hhmm.split(':').map(Number);
    const period = hh >= 12 ? 'PM' : 'AM';
    const h12 = ((hh + 11) % 12) + 1;
    return `${h12}:${String(mm).padStart(2, '0')} ${period}`;
  };
  const eventTimeLabel = (event) => {
    if (event.all_day) return 'All day';
    const start = event.start_date?.match(/T(\d{2}:\d{2})/)?.[1];
    const end = event.end_date?.match(/T(\d{2}:\d{2})/)?.[1];
    if (!start) return '';
    return end ? `${fmtTime(start)} – ${fmtTime(end)}` : fmtTime(start);
  };

  // Get dates that have events for calendar highlighting
  const eventDates = events.map(e => {
    try {
      return parseISO(e.start_date);
    } catch {
      return null;
    }
  }).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="calendar-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6 gap-2">
          <h1 className="text-2xl font-bold text-stone-900">Family Calendar</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={openSubscribeDialog}
              className="rounded-full border-stone-200"
              data-testid="subscribe-calendar-btn"
            >
              <Share2 className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Sync</span>
            </Button>
            {canCreate && (
              <Button
                onClick={() => openCreateDialog(null)}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                data-testid="create-event-btn"
              >
                <Plus className="w-5 h-5 md:mr-2" />
                <span className="hidden md:inline">Add Event</span>
              </Button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Calendar */}
          <div className="card-soft" data-testid="calendar-widget">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-xl"
              modifiers={{
                hasEvent: eventDates
              }}
              modifiersStyles={{
                hasEvent: {
                  backgroundColor: '#CCFBF1',
                  color: '#0D9488',
                  fontWeight: '600'
                }
              }}
            />
          </div>

          {/* Events for Selected Date */}
          <div className="card-soft" data-testid="events-list">
            <h2 className="text-lg font-semibold text-stone-900 mb-4">
              {format(selectedDate, 'MMMM d, yyyy')}
            </h2>

            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 text-teal-600 animate-spin mx-auto" />
              </div>
            ) : selectedDateEvents.length === 0 ? (
              <div className="text-center py-8">
                <CalendarDays className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 mb-4">No events on this day</p>
                {canCreate && (
                  <Button
                    variant="outline"
                    onClick={() => openCreateDialog(selectedDate)}
                    className="rounded-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map(event => (
                  <Link
                    to={`/events/${event.id}`}
                    key={event.id}
                    className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl hover:bg-teal-50/40 transition"
                    data-testid={`event-${event.id}`}
                  >
                    <div className="w-1 h-full min-h-[40px] bg-teal-500 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-stone-900 flex items-center gap-1.5">
                        {event.title}
                        <ArrowRight className="w-3.5 h-3.5 text-stone-300" />
                      </h3>
                      {event.location && (
                        <p className="text-xs text-stone-500 mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {event.location}
                        </p>
                      )}
                      {event.description && (
                        <p className="text-sm text-stone-500 mt-1 line-clamp-1">{event.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {event.all_day ? (
                          <span className="text-xs text-teal-600 font-medium">All day</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-stone-600 font-medium">
                            <Clock className="w-3 h-3" />
                            {eventTimeLabel(event)}
                          </span>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                          className="text-stone-400 hover:text-teal-600 rounded-full h-11 w-11"
                          title="Add to your calendar"
                          data-testid={`download-event-${event.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                        <DropdownMenuItem
                          onClick={(e) => { e.preventDefault(); handleDownloadIcs(event.id, event.title); }}
                          className="cursor-pointer"
                        >
                          <Download className="w-4 h-4 mr-2" /> Apple / Outlook (.ics)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => { e.preventDefault(); window.open(buildGoogleCalendarUrl(event), '_blank', 'noopener'); }}
                          className="cursor-pointer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" /> Google Calendar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Upcoming Events</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events
              .filter(e => {
                try {
                  return parseISO(e.start_date) >= new Date();
                } catch {
                  return false;
                }
              })
              .sort((a, b) => {
                try {
                  return parseISO(a.start_date).getTime() - parseISO(b.start_date).getTime();
                } catch {
                  return 0;
                }
              })
              .slice(0, 6)
              .map(event => (
                <Link to={`/events/${event.id}`} key={event.id} className="card-soft p-4 block hover:shadow-lg transition" data-testid={`upcoming-${event.id}`}>
                  <p className="text-sm text-teal-600 font-medium mb-1">
                    {format(parseISO(event.start_date), 'MMM d, yyyy')}
                    {!event.all_day && (
                      <span className="text-stone-500 font-normal"> · {eventTimeLabel(event)}</span>
                    )}
                  </p>
                  <h3 className="font-semibold text-stone-900">{event.title}</h3>
                  {event.location && (
                    <p className="text-xs text-stone-500 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {event.location}
                    </p>
                  )}
                  {event.description && (
                    <p className="text-sm text-stone-500 mt-1 line-clamp-2">{event.description}</p>
                  )}
                  <div className="mt-3 text-xs text-teal-600 font-medium flex items-center gap-1">
                    View event <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </main>

      <MobileNav />

      {/* Create/Edit Event Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => {
        setCreateDialogOpen(open);
        if (!open) resetEventForm();
      }}>
        <DialogContent className="max-w-md rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="create-event-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-stone-900">
              {editingEventId ? 'Edit Event' : 'Add Event'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eventTitle" className="text-stone-700">Event Title</Label>
              <Input
                id="eventTitle"
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
                placeholder="e.g., Family Dinner"
                className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                required
                data-testid="event-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eventDate" className="text-stone-700">Date</Label>
              <Input
                id="eventDate"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                required
                data-testid="event-date-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="allDay"
                checked={eventAllDay}
                onCheckedChange={setEventAllDay}
                data-testid="event-allday-checkbox"
              />
              <Label htmlFor="allDay" className="text-stone-700 cursor-pointer">All day event</Label>
            </div>

            {!eventAllDay && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="eventStartTime" className="text-stone-700 text-sm flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    Start time
                  </Label>
                  <Input
                    id="eventStartTime"
                    type="time"
                    value={eventStartTime}
                    onChange={(e) => setEventStartTime(e.target.value)}
                    className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                    data-testid="event-start-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eventEndTime" className="text-stone-700 text-sm flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    End time
                  </Label>
                  <Input
                    id="eventEndTime"
                    type="time"
                    value={eventEndTime}
                    onChange={(e) => setEventEndTime(e.target.value)}
                    className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                    data-testid="event-end-time-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="eventLocation" className="text-stone-700 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> Location (optional)
              </Label>
              <Input
                id="eventLocation"
                value={eventLocation}
                onChange={(e) => setEventLocation(e.target.value)}
                placeholder="e.g., Grandma's house, 123 Maple St"
                className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                data-testid="event-location-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eventDescription" className="text-stone-700">Description (optional)</Label>
              <Textarea
                id="eventDescription"
                value={eventDescription}
                onChange={(e) => setEventDescription(e.target.value)}
                placeholder="Add details about this event"
                className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500 resize-none"
                rows={3}
                data-testid="event-description-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating || !eventTitle.trim() || !eventDate}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                data-testid="submit-event-btn"
              >
                {creating ? (editingEventId ? 'Saving...' : 'Creating...') : (editingEventId ? 'Save Changes' : 'Add Event')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subscribe Calendar Dialog */}
      <Dialog open={subscribeDialogOpen} onOpenChange={setSubscribeDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="subscribe-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-stone-900">Sync with Your Calendar</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-stone-700">
              Add your family calendar to Apple Calendar, Google Calendar, or Outlook. New events will appear automatically.
            </p>

            {!subscribeUrls.subscribe_url ? (
              <div className="text-center py-4">
                {isAdmin ? (
                  <Button
                    onClick={handleGenerateSubscribeLink}
                    disabled={generatingToken}
                    className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                    data-testid="generate-subscribe-btn"
                  >
                    {generatingToken ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Share2 className="w-4 h-4 mr-2" /> Generate Subscription Link</>
                    )}
                  </Button>
                ) : (
                  <p className="text-sm text-stone-500 bg-stone-50 rounded-xl p-4">
                    Ask the family admin to generate a subscription link.
                  </p>
                )}
              </div>
            ) : (
              <>
                {/* Quick Add Buttons */}
                <div className="space-y-2" data-testid="quick-add-buttons">
                  <a
                    href={subscribeUrls.webcal_url}
                    className="flex items-center gap-3 p-3 bg-stone-50 hover:bg-stone-100 rounded-2xl border border-stone-200 transition-colors"
                    data-testid="apple-cal-btn"
                  >
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-stone-200">
                      <Apple className="w-5 h-5 text-stone-800" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900">Apple Calendar</p>
                      <p className="text-xs text-stone-500">Opens Calendar on iPhone & Mac</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-stone-400" />
                  </a>

                  <a
                    href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(subscribeUrls.subscribe_url)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-stone-50 hover:bg-stone-100 rounded-2xl border border-stone-200 transition-colors"
                    data-testid="google-cal-btn"
                  >
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-stone-200 font-bold text-blue-600">
                      G
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900">Google Calendar</p>
                      <p className="text-xs text-stone-500">Add to your Google account</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-stone-400" />
                  </a>

                  <a
                    href={`https://outlook.live.com/calendar/0/addcalendar?url=${encodeURIComponent(subscribeUrls.subscribe_url)}&name=${encodeURIComponent('Family Calendar')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-stone-50 hover:bg-stone-100 rounded-2xl border border-stone-200 transition-colors"
                    data-testid="outlook-cal-btn"
                  >
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-stone-200 font-bold text-blue-500">
                      O
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900">Outlook Calendar</p>
                      <p className="text-xs text-stone-500">Add to Microsoft Outlook</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-stone-400" />
                  </a>
                </div>

                {/* Manual URL */}
                <div className="pt-4 border-t border-stone-100">
                  <Label className="text-stone-700 text-sm mb-2 block">Or copy the subscription URL:</Label>
                  <div className="flex gap-2">
                    <Input
                      value={subscribeUrls.subscribe_url}
                      readOnly
                      className="flex-1 rounded-xl text-xs"
                      data-testid="subscribe-url-input"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copySubscribeUrl}
                      className="rounded-xl"
                      data-testid="copy-subscribe-url-btn"
                    >
                      {copiedUrl ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">
                    Paste this URL into your calendar app's "Add by URL" option.
                  </p>
                </div>

                {isAdmin && (
                  <div className="pt-2">
                    <button
                      onClick={handleGenerateSubscribeLink}
                      disabled={generatingToken}
                      className="text-xs text-stone-500 hover:text-stone-700 underline"
                      data-testid="rotate-token-btn"
                    >
                      {generatingToken ? 'Rotating...' : 'Rotate link (revokes old URL)'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
